// overlayKit/ComparisonChain.tsx — production-universal "constant slides through frames of equivalents".
//
// Replaces the hand-tuned sliding chain pattern from UraniumSeg1.tsx. The constant term (e.g.
// "10 g URANIUM") never fades; it SLIDES between frame positions. Each frame can host 0..N
// equivalents (e.g. "1 TONNE OF COAL", "149 GAL OF OIL") that fade in/out independently.
//
// Production-universal: palette via context, dimensions via useVideoConfig() (so the same
// component renders correctly at 1280×704, 720×1280, or 3840×2160).
import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { appear, clamp, FONT, Layout, layoutX, TS } from "./utils";
import { resolveColor, usePalette } from "./palette";

export type ChainEquivalent = {
  label: string;
  /** Static text (e.g. "1 TONNE", "149 GAL") or a number. */
  value: string | number;
  /** Palette token. */
  color: string;
  /** Override the auto-pick. If a frame uses layout="custom", equivalents MUST set this or `x`. */
  layout?: Layout;
  /** Absolute x in px — only when layout="custom" or to override. */
  x?: number;
  /** Fade-in start (s, absolute). */
  atS: number;
  /** Fade-out end (s, absolute). */
  outS: number;
};

export type ChainFrame = {
  layout: Layout;
  /** Only when layout="custom". */
  x?: number;
  /** Absolute time the constant ARRIVES at this position. */
  landAt: number;
  /** Duration of the slide INTO this frame, in seconds. Default 0.6. First frame ignores this. */
  slideDurationS?: number;
  equivalents: ChainEquivalent[];
};

export type ConstantTerm = {
  label: string;
  /** Static value text shown beneath the label (or above, depending on layout). */
  value?: string | number;
  suffix?: string;
  /** Animated count-up overriding static value. Locks at endS. */
  count?: { from: number; to: number; atS: number; endS: number };
  color: string;
  /** Time the constant first fades in. Default = frames[0].landAt. */
  fadeInS?: number;
  /** Fade-in duration. Default 0.35s. */
  fadeInDurS?: number;
};

export type ComparisonChainProps = {
  constant: ConstantTerm;
  frames: ChainFrame[];
  /** Y position in px. Default = height × 0.774 (the lower-third band, =545 at h=704). */
  y?: number;
  /** Palette token for the "=" signs. Default "secondary". */
  eqSignColor?: string;
};

// Layout fallbacks when an equivalent doesn't set its own layout.
// (frame layout) → ordered list of slot layouts to assign to its equivalents.
const AUTO_EQUIV_SLOTS: Partial<Record<Layout, Layout[]>> = {
  "left-half":    ["right-half"],
  "right-half":   ["left-half"],
  "left-third":   ["center-third", "right-third"],
  "center-third": ["right-third", "left-third"],
  "right-third":  ["center-third", "left-third"],
  "center":       ["right-half"],
};

const Term: React.FC<{
  x: number; y: number; opacity: number;
  value: string; label: string; colorHex: string;
}> = ({ x, y, opacity, value, label, colorHex }) => (
  <div
    style={{
      position: "absolute",
      left: x,
      top: y,
      transform: "translate(-50%,-50%)",
      opacity,
      textAlign: "center",
      fontFamily: FONT,
      whiteSpace: "nowrap",
    }}
  >
    <div
      style={{
        color: colorHex,
        fontSize: 60,
        fontWeight: 800,
        letterSpacing: "-1px",
        lineHeight: 1,
        textShadow: TS,
      }}
    >
      {value}
    </div>
    <div
      style={{
        color: colorHex,
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textShadow: TS,
        marginTop: 7,
      }}
    >
      {label}
    </div>
  </div>
);

const Eq: React.FC<{ x: number; y: number; opacity: number; colorHex: string }> = ({
  x, y, opacity, colorHex,
}) => (
  <div
    style={{
      position: "absolute",
      left: x,
      top: y - 12,
      transform: "translate(-50%,-50%)",
      opacity,
      color: colorHex,
      fontSize: 50,
      fontWeight: 800,
      textShadow: TS,
      fontFamily: FONT,
    }}
  >
    =
  </div>
);

export const ComparisonChain: React.FC<ComparisonChainProps> = ({
  constant, frames, y, eqSignColor = "secondary",
}) => {
  const f = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const t = f / fps;
  const palette = usePalette();   // one hook call, all colors resolved via this dict

  if (frames.length === 0) return null;

  const yPos = y ?? Math.round(height * 0.774);
  const constColorHex = resolveColor(palette, constant.color);
  const eqSignHex = resolveColor(palette, eqSignColor);

  // ── Build the constant's x interpolation keyframes ─────────────────────────
  // Pattern: [hold @ frame0.landAt, depart @ frame1.landAt - dur, arrive @ frame1.landAt, depart @ frame2 ..., arrive @ frameN]
  const xTimes: number[] = [];
  const xVals: number[] = [];
  frames.forEach((fr, i) => {
    const fx = layoutX(fr.layout, width, fr.x);
    if (i === 0) {
      xTimes.push(fr.landAt);
      xVals.push(fx);
    } else {
      const dur = fr.slideDurationS ?? 0.6;
      // Depart from previous position at landAt - dur
      xTimes.push(fr.landAt - dur);
      xVals.push(xVals[xVals.length - 1]);
      // Arrive at new position at landAt
      xTimes.push(fr.landAt);
      xVals.push(fx);
    }
  });
  const constX = interpolate(t, xTimes, xVals, clamp);

  // ── Constant fades in once, then fades out when the chain ends ────────────
  // Fade-out is auto-derived from the latest equivalent's outS (across all
  // frames). If the chain has no equivalents at all, the constant stays.
  const fadeInS = constant.fadeInS ?? frames[0].landAt;
  const fadeInDur = constant.fadeInDurS ?? 0.35;
  const allEqOutS = frames.flatMap((fr) => fr.equivalents.map((eq) => eq.outS));
  const lastOutS = allEqOutS.length > 0 ? Math.max(...allEqOutS) : Infinity;
  const constOp = lastOutS === Infinity
    ? appear(t, fadeInS, fadeInDur)
    : interpolate(
        t,
        [fadeInS, fadeInS + fadeInDur, lastOutS - 0.4, lastOutS],
        [0, 1, 1, 0],
        clamp,
      );

  // ── Constant value (count-up overrides static) ─────────────────────────────
  let constValueText = "";
  if (constant.count) {
    const v = interpolate(t, [constant.count.atS, constant.count.endS],
                          [constant.count.from, constant.count.to], clamp);
    // Render integer if both endpoints are integers, otherwise 1-decimal
    const isInt = Number.isInteger(constant.count.from) && Number.isInteger(constant.count.to);
    constValueText = `${Math.round(v).toString()}${constant.suffix ?? ""}`;
    if (!isInt) constValueText = `${v.toFixed(1)}${constant.suffix ?? ""}`;
  } else if (constant.value != null) {
    constValueText = `${constant.value}${constant.suffix ?? ""}`;
  }

  // ── Resolve each equivalent's x AND the "=" midpoint that precedes it ─────
  // Within a frame, the chain reads:  CONSTANT  =  eq[0]  =  eq[1]  =  eq[2]  ...
  // So the "=" sign before eq[i] sits between (constant if i==0 else eq[i-1]) and eq[i].
  // Eq-sign x is FIXED (computed from each frame's LANDED constant x — never the live moving
  // constX), because by the time each equivalent fades in, the constant has finished sliding.
  type Resolved = ChainEquivalent & { _x: number; _eqMidX: number; _frameIdx: number };
  const resolved: Resolved[] = [];
  frames.forEach((fr, frIdx) => {
    const auto = AUTO_EQUIV_SLOTS[fr.layout] ?? [];
    const constLandedX = layoutX(fr.layout, width, fr.x);
    let prevX = constLandedX;   // chain anchor for this frame
    fr.equivalents.forEach((eq, eqIdx) => {
      let ex: number;
      if (eq.x != null) {
        ex = eq.x;
      } else if (eq.layout) {
        ex = layoutX(eq.layout, width, eq.x);
      } else if (fr.layout === "custom") {
        throw new Error(
          `ComparisonChain: frame ${frIdx} has layout="custom" — equivalent ${eqIdx} must set x or layout`
        );
      } else {
        const slot = auto[eqIdx];
        if (!slot) {
          throw new Error(
            `ComparisonChain: frame ${frIdx} (layout=${fr.layout}) ran out of auto-slots ` +
              `for equivalent ${eqIdx} (only ${auto.length} available) — set x or layout explicitly`
          );
        }
        ex = layoutX(slot, width);
      }
      const eqMidX = (prevX + ex) / 2;
      resolved.push({ ...eq, _x: ex, _eqMidX: eqMidX, _frameIdx: frIdx });
      prevX = ex;
    });
  });

  return (
    <>
      {/* CONSTANT — slides through frames, never fades out */}
      <Term
        x={constX}
        y={yPos}
        opacity={constOp}
        value={constValueText}
        label={constant.label}
        colorHex={constColorHex}
      />

      {/* EQUIVALENTS — each fades in/out independently, "=" sign between */}
      {resolved.map((eq, i) => {
        const op = interpolate(
          t,
          [eq.atS, eq.atS + 0.22, eq.outS - 0.35, eq.outS],
          [0, 1, 1, 0],
          clamp
        );
        if (op <= 0.001) return null;
        const eqHex = resolveColor(palette, eq.color);
        return (
          <React.Fragment key={i}>
            <Eq x={eq._eqMidX} y={yPos} opacity={op} colorHex={eqSignHex} />
            <Term
              x={eq._x}
              y={yPos}
              opacity={op}
              value={`${eq.value}`}
              label={eq.label}
              colorHex={eqHex}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
